import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import {
  useCreateExpense,
  useExpenses,
  useSubmitExpense
} from "../../../hooks/api/education/usePlatform";

const ExpensePage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { data: expenses = [], isPending, refetch } = useExpenses({ size: 50 });
  const createMutation = useCreateExpense();
  const submitMutation = useSubmitExpense();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-expenses", "Loading expenses...")}
      />
    );
  }

  return (
    <Box data-testid="expense-page" sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">{t("expenses-title", "Expenses")}</Typography>
        <AppButton onClick={() => setOpen(true)}>
          {t("new-expense", "New expense")}
        </AppButton>
      </Box>

      {expenses.length === 0 ? (
        <Typography color="text.secondary">
          {t("expenses-empty", "No expense requests yet.")}
        </Typography>
      ) : (
        expenses.map((expense) => (
          <Box
            key={expense.id}
            sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Typography fontWeight={600}>{expense.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {expense.amount} {expense.currency} · {expense.status}
            </Typography>
            {expense.status === "DRAFT" && (
              <AppButton
                size="small"
                sx={{ mt: 1 }}
                onClick={async () => {
                  await submitMutation.mutateAsync({ expenseId: expense.id! });
                  refetch();
                }}
              >
                {t("submit", "Submit")}
              </AppButton>
            )}
          </Box>
        ))
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("new-expense", "New expense")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("title", "Title")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("category", "Category")}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("amount", "Amount")}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <AppButton variant="secondary" onClick={() => setOpen(false)}>
            {t("cancel", "Cancel")}
          </AppButton>
          <AppButton
            disabled={!title || !amount || createMutation.isPending}
            onClick={async () => {
              await createMutation.mutateAsync({
                title,
                category,
                amount: Number(amount)
              });
              setOpen(false);
              setTitle("");
              setAmount("");
              refetch();
            }}
          >
            {t("create", "Create")}
          </AppButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpensePage;
